import React from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { ITableProps } from "../../types/table";
import Pagination, { PaginationProps } from "./Pagination";

export interface TableProps<
  T extends { id?: string | number },
> extends ITableProps<T> {
  loading?: boolean;
  pagination?: PaginationProps;
}

const CustomTable = <T extends { id?: string | number }>({
  columns,
  data,
  emptyMessage = "No data available",
  renderRow,
  onRowClick,
  loading = false,
  pagination,
}: TableProps<T>) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: "#4285F4" }} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ borderRadius: "12px", overflow: "hidden" }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
            {columns.map((column) => (
              <TableCell
                key={String(column.id)}
                align={column.align}
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  width: column.width,
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, index) => (
            <TableRow
              key={item.id !== undefined ? String(item.id) : index}
              hover
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              sx={{
                cursor: onRowClick ? "pointer" : "default",
                "&:hover": { backgroundColor: "background.paper" },
              }}
            >
              {renderRow
                ? renderRow(item, index)
                : columns.map((column) => (
                    <TableCell key={String(column.id)} align={column.align}>
                      {column.render
                        ? column.render(item)
                        : (item[column.id] as React.ReactNode)}
                    </TableCell>
                  ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pagination && pagination.totalCount > pagination.pageSize && (
        <Pagination {...pagination} />
      )}
    </TableContainer>
  );
};

export default CustomTable;
